import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { Select } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
    Upload,
    FileText,
    Download,
    AlertCircle,
    CheckCircle,
    RefreshCw,
    Users,
    Building2,
    UserCog,
    ArrowRight,
    ChevronDown
} from "lucide-react";
import { toast } from "sonner";
import {
    bulkAssignLeads,
    createLeadBatch,
    bulkDeleteLeads
} from "../../services/crmServices/leadService";
import { DuplicateReviewPanel } from "./DuplicateReviewPanel";
import { fetchAvailableDesks } from "../../services/crmServices/deskService";
import { fetchLeadStatuses } from "../../services/crmServices/leadStatusService";
import { searchUsers, fetchAvailableUsers } from "../../services/crmServices/userService";
import { getFullName } from "../../services/common/util";
import { useAuth } from "../../hooks/useAuth";
import { validatePhoneNumber, convertScientificNotation } from "../../utils/phoneUtils";

export function LeadImport({ onClose, onSuccess }) {
    const { user } = useAuth();
    const [file, setFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [csvData, setCsvData] = useState([]);
    const [validationResults, setValidationResults] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    // Progress tracking state
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percentage: 0 });
    const [defaultDesk, setDefaultDesk] = useState(null);
    const [defaultStatus, setDefaultStatus] = useState("new");
    const [defaultCountry, setDefaultCountry] = useState("");
    const [showAssignmentOptions, setShowAssignmentOptions] = useState(false);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedFloorManager, setSelectedFloorManager] = useState("");
    const [selectedDesk, setSelectedDesk] = useState("");
    const [assignmentType, setAssignmentType] = useState("agent"); // "agent", "desk", or "floorManager"
    const [availableAgents, setAvailableAgents] = useState([]);
    const [availableFloorManagers, setAvailableFloorManagers] = useState([]);
    const [availableGeneralManagers, setAvailableGeneralManagers] = useState([]);
    const [availableDesks, setAvailableDesks] = useState([]);
    const isAdmin = user?.position?.role === "admin";
    const [leadStatuses, setLeadStatuses] = useState([]);
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [duplicateLeads, setDuplicateLeads] = useState([]);
    const [notesExpanded, setNotesExpanded] = useState(false);
    const [createdLeadIds, setCreatedLeadIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    // Load desks when component mounts
    useEffect(() => {
        fetchAvailableDesks().then((res) => {
            if (res?.data?.length) {
                setAvailableDesks(res.data);
            }
        });
        fetchLeadStatuses().then((res) => {
            if (res?.data?.length) {
                setLeadStatuses(res.data);
            }
        });
    }, []);

    // Fetch floor managers when admin opens assignment options or selects floorManager type
    useEffect(() => {
        if (isAdmin && showAssignmentOptions) {
            // Always fetch when assignment options are shown (will skip if already loaded)
            if (availableFloorManagers.length === 0) {
                fetchFloorManagers();
            }
        }
    }, [isAdmin, showAssignmentOptions]);

    // Also fetch when floorManager type is selected (in case it wasn't loaded before)
    useEffect(() => {
        if (isAdmin && assignmentType === "floorManager" && availableFloorManagers.length === 0) {
            fetchFloorManagers();
        }
    }, [assignmentType, isAdmin]);


    useEffect(() => {
        // If user is a manager, fetch agents from their desk
        if (user.position.role === "deskManager" && availableDesks?.length > 0) {
            let agents = [];
            availableDesks.forEach((desk) => {
                if (desk.agents && desk.agents.length > 0) {
                    agents = [...agents, ...desk.agents];
                }
            });
            setAvailableAgents(agents);
        }
        // If user is admin, fetch all agents
        if (isAdmin) {
            searchUsers({}).then((res) => {
                if (res?.data?.length) {
                    setAvailableAgents(res.data);
                }
            });
        }
    }, [availableDesks, isAdmin]);

    const fetchFloorManagers = async () => {
        try {
            const response = await fetchAvailableUsers();
            if (response?.data) {
                // Filter for floor managers only
                const floorManagerUsers = response.data.filter((user) => {
                    const role = user.position?.role || user.position;
                    return role === "floorManager";
                });
                setAvailableFloorManagers(floorManagerUsers || []);
            } else {
                setAvailableFloorManagers([]);
            }
        } catch (error) {
            console.error("Error fetching floor managers:", error);
            toast.error("Failed to load floor managers");
            setAvailableFloorManagers([]);
        }
    };

    const fetchGeneralManagers = async () => {
        try {
            const response = await fetchAvailableUsers();
            if (response?.data) {
                // Filter for general managers only
                const generalManagerUsers = response.data.filter((user) => {
                    const role = user.position?.role || user.position;
                    return role === "generalManager";
                });
                setAvailableGeneralManagers(generalManagerUsers || []);
            } else {
                setAvailableGeneralManagers([]);
            }
        } catch (error) {
            console.error("Error fetching general managers:", error);
            toast.error("Failed to load general managers");
            setAvailableGeneralManagers([]);
        }
    };



    const parseCSV = (csvText) => {
        // Properly parse CSV handling quoted fields that may contain newlines
        const rows = [];
        let currentRow = "";
        let inQuotes = false;
        let i = 0;
        
        while (i < csvText.length) {
            const char = csvText[i];
            const nextChar = i + 1 < csvText.length ? csvText[i + 1] : "";
            
            if (char === '"') {
                // Handle escaped quotes ("")
                if (nextChar === '"' && inQuotes) {
                    currentRow += '""';
                    i += 2;
                    continue;
                }
                inQuotes = !inQuotes;
                currentRow += char;
            } else if (char === '\n' || char === '\r') {
                // Only treat as row separator if we're not inside quotes
                if (!inQuotes) {
                    // Skip \r\n combination
                    if (char === '\r' && nextChar === '\n') {
                        i += 2;
                    } else {
                        i++;
                    }
                    
                    // Add the completed row
                    if (currentRow.trim()) {
                        rows.push(currentRow);
                    }
                    currentRow = "";
                    continue;
                } else {
                    // Inside quotes, preserve the newline as part of the field
                    currentRow += char;
                }
            } else {
                currentRow += char;
            }
            
            i++;
        }
        
        // Add the last row if it exists
        if (currentRow.trim()) {
            rows.push(currentRow);
        }
        
        if (rows.length < 2) {
            throw new Error("CSV file must have at least a header row and one data row");
        }

        const headers = parseCSVLine(rows[0]).map(h => h.trim());
        const data = [];

        for (let i = 1; i < rows.length; i++) {
            const values = parseCSVLine(rows[i]).map(v => v ? v.trim() : "");
            if (values.length > 0 && values.some((val) => val.trim())) {
                const row = {};
                // Only map values to headers that exist - prevent misalignment
                headers.forEach((header, index) => {
                    if (header) {
                        // Only assign if we have a value at this index
                        row[header] = index < values.length ? values[index] : "";
                    }
                });
                // If there are more values than headers, they're likely in unmapped columns
                // We'll ignore them unless they match known column names
                data.push(row);
            }
        }

        return data;
    };

    const handleAssignLeads = async () => {
        if (assignmentType === "agent" && selectedAgents.length === 0) {
            toast.error("Please select at least one agent to assign leads");
            return;
        }
        if (assignmentType === "desk" && !selectedDesk) {
            toast.error("Please select a desk to assign leads");
            return;
        }
        if (assignmentType === "floorManager" && !selectedFloorManager) {
            toast.error("Please select a floor manager to assign leads");
            return;
        }

        setImporting(true);

        try {
            // First create all leads
            let createdLeads = [];
            let errorCount = 0;

            // Add assignment fields to leads if specified
            const leadsToCreate = validationResults.map((result) => {
                const lead = { ...result.lead };
                if (assignmentType === "floorManager" && selectedFloorManager) {
                    lead.floorManager = selectedFloorManager;
                    lead.desk = null;
                    lead.agent = null;
                } else if (assignmentType === "desk" && selectedDesk) {
                    lead.desk = selectedDesk;
                    lead.agent = null;
                }
                // For agent assignment, we'll assign after creation
                return lead;
            });

            const response = await createLeadBatch(leadsToCreate);

            if (response?.status === 200) {
                createdLeads = response.data.leads;
                const duplicateCount = response.data.duplicateCount || 0;

                if (createdLeads.length > 0) {
                    setCreatedLeadIds(createdLeads.map((l) => l._id));
                }
                
                // Store duplicate leads for export
                if (response.data.duplicateLeads && response.data.duplicateLeads.length > 0) {
                    setDuplicateLeads(response.data.duplicateLeads);
                }
                
                // Then assign leads based on assignment type
                if (createdLeads.length > 0 && assignmentType === "agent") {
                    const leadIds = createdLeads.map((createdLead) => createdLead._id);
                    const agentIds = selectedAgents;

                    const assignmentData = {
                        leadIds,
                        agentIds
                    };

                    const assignResponse = await bulkAssignLeads(assignmentData);

                    if (assignResponse?.status === 200) {
                        let message = `Successfully imported and assigned ${createdLeads.length} leads to ${selectedAgents.length} agents!`;
                        if (duplicateCount > 0) {
                            message += ` ${duplicateCount} duplicate(s) skipped. Review them below.`;
                        }
                        toast.success(message);
                    } else {
                        toast.error("Failed to assign leads to agents");
                    }
                } else if (createdLeads.length > 0) {
                    // For floorManager and desk, assignment is already done during creation
                    const assignmentTypeLabel =
                        assignmentType === "floorManager" ? "floor manager" : assignmentType === "desk" ? "desk" : "";
                    let message = `Successfully imported and assigned ${createdLeads.length} leads to ${assignmentTypeLabel}!`;
                    if (duplicateCount > 0) {
                        message += ` ${duplicateCount} duplicate(s) skipped. Review them below.`;
                    }
                    toast.success(message);
                } else if (duplicateCount > 0) {
                    // All leads were duplicates
                    toast.warning(`All ${duplicateCount} leads were duplicates and skipped. Review them below.`);
                }

                if (errorCount > 0) {
                    toast.warning(`${errorCount} leads failed to import`);
                }

                // Don't close modal if there are duplicates to review
                if (duplicateCount > 0) {
                    // Keep modal open so user can review duplicates
                    return;
                }

                // Close modal after successful import (only if no duplicates)
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 1500);
            } else {
                toast.error("Failed to import leads");
            }
        } catch (error) {
            console.error("Import error:", error);
            toast.error("Failed to import and assign leads");
        } finally {
            setImporting(false);
        }
    };

    const parseCSVLine = (line) => {
        const result = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = i + 1 < line.length ? line[i + 1] : "";

            if (char === '"') {
                // Handle escaped quotes ("")
                if (nextChar === '"' && inQuotes) {
                    current += '"';
                    i++; // Skip the next quote
                    continue;
                }
                inQuotes = !inQuotes;
                // Don't add the quote character itself to the current value
            } else if (char === "," && !inQuotes) {
                result.push(current);
                current = "";
            } else {
                current += char;
            }
        }

        result.push(current);
        return result;
    };

    // Update the mapCSVToLead function to include secondPhone and source
    const mapCSVToLead = (csvRow, fileName = null) => {
        // Map CSV columns to lead object structure (case-insensitive matching)
        // IMPORTANT: Order matters - "extra" related fields should be checked first
        const mapping = {
            // Extra/Notes fields - must be checked first to prevent mis-mapping
            "extra": "extra",
            "notes": "extra",
            // Internal Notes (admin-only) - must be checked before name fields
            "internal notes": "internalNotes",
            "internal_notes": "internalNotes",
            "internalnotes": "internalNotes",
            // Name fields
            "first name": "firstName",
            "firstname": "firstName",
            "first_name": "firstName",
            "last name": "lastName",
            "lastname": "lastName",
            "last_name": "lastName",
            // Contact fields
            "email": "email",
            "phone": "phone",
            "second phone": "secondPhone",
            "secondphone": "secondPhone",
            "second_phone": "secondPhone",
            // Other fields
            "country": "country",
            "status": "status",
            "desk": "desk",
            "source": "source",
            "campaign": "source"
        };

        const lead = {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            secondPhone: "",
            country: defaultCountry,
            status: defaultStatus,
            desk: defaultDesk,
            source: fileName || "", // Set source to filename if provided, otherwise empty
            extra: "",
            internalNotes: ""
        };

        // STEP 1: ISOLATE "extra" and "internalNotes" columns completely
        // Identify which columns are "extra" or "internalNotes" by header name only.
        // Column-based isolation is sufficient because the CSV parser already maps
        // each value to the correct column by index.
        const extraColumnKeys = new Set();
        const internalNotesColumnKeys = new Set();
        let extraContent = "";
        let internalNotesContent = "";

        Object.keys(csvRow).forEach((csvKey) => {
            const normalizedKey = csvKey.trim().toLowerCase();
            const leadKey = mapping[normalizedKey];

            if (leadKey === "extra") {
                extraColumnKeys.add(csvKey);
                const value = csvRow[csvKey] ? csvRow[csvKey].trim() : "";
                if (value) {
                    extraContent = extraContent ? `${extraContent}\n${value}` : value;
                }
            }

            if (leadKey === "internalNotes") {
                internalNotesColumnKeys.add(csvKey);
                const value = csvRow[csvKey] ? csvRow[csvKey].trim() : "";
                if (value) {
                    internalNotesContent = internalNotesContent ? `${internalNotesContent}\n${value}` : value;
                }
            }
        });
        
        // Set extra content if we collected any
        if (extraContent) {
            lead.extra = extraContent;
        }
        
        // Set internal notes content if we collected any
        if (internalNotesContent) {
            lead.internalNotes = internalNotesContent;
        }
        
        // STEP 2: Process all OTHER fields (completely excluding extra and internalNotes columns)
        Object.keys(csvRow).forEach((csvKey) => {
            // CRITICAL: Skip if this column is an "extra" or "internalNotes" column
            if (extraColumnKeys.has(csvKey) || internalNotesColumnKeys.has(csvKey)) {
                return; // Completely skip these columns
            }
            
            const normalizedKey = csvKey.trim().toLowerCase();
            const leadKey = mapping[normalizedKey];
            
            // Skip if no mapping exists or if it maps to "extra" or "internalNotes"
            if (!leadKey || leadKey === "extra" || leadKey === "internalNotes") {
                return;
            }
            
            // Get the value
            let value = csvRow[csvKey] ? csvRow[csvKey].trim() : "";
            if (!value) {
                return;
            }
            
            // Convert Excel scientific notation for phone fields (e.g., "4.47E11" -> "447000000000")
            if (leadKey === "phone" || leadKey === "secondPhone") {
                value = convertScientificNotation(value);
            }

            // Skip if this field is already set (to prevent overwriting)
            if (lead[leadKey] && lead[leadKey].trim() !== "") {
                return;
            }
            
            if (leadKey === "desk") {
                // Find desk by name
                const desk = availableDesks.find((d) => d.name.toLowerCase() === value.toLowerCase());
                lead[leadKey] = desk ? desk._id : defaultDesk;
            } else if (leadKey === "status") {
                // Validate status
                const statusOption = leadStatuses.find(
                    (s) =>
                        s.label.toLowerCase() === value.toLowerCase() ||
                        s.value.toLowerCase() === value.toLowerCase()
                );
                lead[leadKey] = statusOption ? statusOption.value : defaultStatus;
            } else {
                // For all other fields, use the trimmed value
                lead[leadKey] = value;
            }
        });

        // Always set source to filename when importing (file name is the source)
        // If fileName is provided, use it as the source (this is the import file name)
        if (fileName) {
            // Extract just the filename without extension for cleaner source name
            const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, "");
            // Source should always be the filename when importing
            // If CSV has a source/campaign column, it will be overwritten by the filename
            lead.source = fileNameWithoutExt;
        }

        return lead;
    };

    // Process lead for country auto-detection only (no validation)
    const processLeadForCountryDetection = (lead) => {
        // Phone number country detection (primary)
        if (lead.phone && lead.phone.trim() !== "") {
            const phoneValidation = validatePhoneNumber(lead.phone);
            
            if (phoneValidation.isValid) {
                // Auto-detect and set country if not already set
                if (phoneValidation.detectedCountry && (!lead.country || lead.country.trim() === "")) {
                    lead.country = phoneValidation.detectedCountry;
                    lead.detectedFromPhone = true; // Flag to show this was auto-detected
                }
                // Format the phone number
                lead.phone = phoneValidation.formattedPhone;
            }
        }

        // Secondary phone country detection (if provided)
        if (lead.secondPhone && lead.secondPhone.trim() !== "") {
            const secondPhoneValidation = validatePhoneNumber(lead.secondPhone);
            
            if (secondPhoneValidation.isValid) {
                // Format the second phone number
                lead.secondPhone = secondPhoneValidation.formattedPhone;
                
                // If primary phone didn't detect country but second phone did
                if (secondPhoneValidation.detectedCountry && (!lead.country || lead.country.trim() === "")) {
                    lead.country = secondPhoneValidation.detectedCountry;
                    lead.detectedFromPhone = true;
                }
            }
        }

        return lead;
    };

    const processFile = async (selectedFile) => {
        if (!selectedFile) return;

        if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith(".csv")) {
            toast.error("Please select a CSV file");
            return;
        }

        if (selectedFile.size > 20 * 1024 * 1024) {
            // 20MB limit
            toast.error("File size must be less than 20MB");
            return;
        }

        setFile(selectedFile);

        try {
            const text = await selectedFile.text();
            const parsedData = parseCSV(text);
            setCsvData(parsedData);

            // Get filename without extension for source
            const fileName = selectedFile.name.replace(/\.[^/.]+$/, "");

            // Map all CSV data to leads and process for country detection
            const results = parsedData.map((row, index) => {
                const lead = mapCSVToLead(row, fileName);
                // Process for country auto-detection only
                const processedLead = processLeadForCountryDetection(lead);
                
                // Check for scientific notation in original phone values
                const originalPhone = row.phone || row.Phone || "";
                const originalSecondPhone = row.secondPhone || row["Second Phone"] || row["second phone"] || "";
                const hasScientificNotation = 
                    /^[\d.]+[Ee][+-]?\d+$/.test(originalPhone) || 
                    /^[\d.]+[Ee][+-]?\d+$/.test(originalSecondPhone);
                
                return {
                    isValid: true, // All leads are valid (no validation)
                    errors: hasScientificNotation ? ["Phone number was in scientific notation - may have lost precision"] : [],
                    rowIndex: index + 1,
                    lead: processedLead,
                    hasScientificNotation: hasScientificNotation
                };
            });

            // Check if any leads have scientific notation and show warning
            const scientificNotationCount = results.filter(r => r.hasScientificNotation).length;
            if (scientificNotationCount > 0) {
                toast.warning(
                    `${scientificNotationCount} phone number(s) were in Excel scientific notation format. ` +
                    `They have been converted, but some digits may have been lost. ` +
                    `Please format Phone column as TEXT in Excel before saving CSV.`,
                    { duration: 8000 }
                );
            }

            // Check for duplicates WITHIN the Excel file itself (same phone number appearing multiple times)
            const phoneNumberMap = new Map(); // Track phone numbers and their first occurrence index
            const excelDuplicateIndices = new Set(); // Track indices of duplicate rows
            let excelDuplicateCount = 0;

            results.forEach((result, index) => {
                const phone = result.lead.phone?.trim();
                const secondPhone = result.lead.secondPhone?.trim();
                
                // Check primary phone
                if (phone) {
                    if (phoneNumberMap.has(phone)) {
                        // This phone number already exists in Excel - mark as duplicate
                        excelDuplicateIndices.add(index);
                        excelDuplicateCount++;
                    } else {
                        // First occurrence of this phone number
                        phoneNumberMap.set(phone, index);
                    }
                }
                
                // Check second phone (only if primary phone didn't mark it as duplicate)
                if (secondPhone && !excelDuplicateIndices.has(index)) {
                    if (phoneNumberMap.has(secondPhone)) {
                        // This phone number already exists in Excel - mark as duplicate
                        excelDuplicateIndices.add(index);
                        excelDuplicateCount++;
                    } else {
                        // First occurrence of this phone number
                        phoneNumberMap.set(secondPhone, index);
                    }
                }
            });

            // Filter out Excel duplicates (keep only first occurrence of each phone number)
            const uniqueResults = results.filter((result, index) => !excelDuplicateIndices.has(index));

            // Show warning if Excel duplicates were found
            if (excelDuplicateCount > 0) {
                toast.warning(
                    `${excelDuplicateCount} lead(s) in Excel had duplicate phone numbers and were removed. ` +
                    `Only the first occurrence of each phone number was kept.`,
                    { duration: 8000 }
                );
            }

            setValidationResults(uniqueResults);
            setShowPreview(true);
        } catch (error) {
            console.error("Error parsing CSV:", error);
            toast.error(`Error parsing CSV: ${error.message}`);
            setFile(null);
        }
    };

    const handleFileSelect = async (e) => {
        const selectedFile = e.target.files?.[0];
        await processFile(selectedFile);
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Check if we're leaving the drop zone (not just moving to a child element)
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];
            await processFile(droppedFile);
        }
    };

    const handleImport = async () => {
        const allLeads = validationResults.map((result) => result.lead);

        if (allLeads.length === 0) {
            toast.error("No leads to import");
            return;
        }

        // If admin or deskManager, show assignment options
        if ((user.position.role === "deskManager" || isAdmin) && allLeads.length > 0) {
            setShowAssignmentOptions(true);
            return;
        }

        await processImport(validationResults);
    };


    const processImport = async (validationResults) => {
        // Extract leads from validation results
        let leadsToImport = validationResults.map((result) => result.lead);
        
        // Apply desk or floor manager assignment if selected
        if (selectedDesk) {
            leadsToImport = leadsToImport.map((lead) => ({
                ...lead,
                desk: selectedDesk,
                agent: null
            }));
        } else if (selectedFloorManager) {
            leadsToImport = leadsToImport.map((lead) => ({
                ...lead,
                floorManager: selectedFloorManager,
                desk: null,
                agent: null
            }));
        }
        
        // Validate maximum upload limit
        const MAX_UPLOAD_LIMIT = 250000;
        if (leadsToImport.length > MAX_UPLOAD_LIMIT) {
            toast.error(`Cannot upload more than ${MAX_UPLOAD_LIMIT} leads at once. Please split your file into smaller batches.`);
            return;
        }

        setImporting(true);
        const totalLeads = leadsToImport.length;
        setUploadProgress({ current: 0, total: totalLeads, percentage: 0 });

        try {
            let successCount = 0;
            let errorCount = 0;
            let totalDuplicateCount = 0;
            const allDuplicateLeads = [];
            const allCreatedIds = [];

            // Process leads in chunks for better progress tracking
            const chunkSize = 50; // Process 50 leads at a time
            const chunks = [];
            for (let i = 0; i < leadsToImport.length; i += chunkSize) {
                chunks.push(leadsToImport.slice(i, i + chunkSize));
            }

            let processedCount = 0;

            for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
                const chunk = chunks[chunkIndex];

                try {
                    const response = await createLeadBatch(chunk);

                    if (response?.status === 200) {
                        successCount += response.data.successCount || 0;
                        errorCount += response.data.errorCount || 0;
                        totalDuplicateCount += response.data.duplicateCount || 0;
                        if (response.data.leads?.length > 0) {
                            allCreatedIds.push(...response.data.leads.map((l) => l._id));
                        }
                        if (response.data.duplicateLeads && response.data.duplicateLeads.length > 0) {
                            allDuplicateLeads.push(...response.data.duplicateLeads);
                        }
                        processedCount += chunk.length;
                    } else {
                        errorCount += chunk.length;
                        processedCount += chunk.length;
                    }
                } catch (error) {
                    console.error(`Error processing chunk ${chunkIndex + 1}:`, error);
                    errorCount += chunk.length;
                    processedCount += chunk.length;
                }

                // Update progress
                const percentage = Math.round((processedCount / totalLeads) * 100);
                setUploadProgress({
                    current: processedCount,
                    total: totalLeads,
                    percentage: percentage
                });
            }

            if (allCreatedIds.length > 0) {
                setCreatedLeadIds(allCreatedIds);
            }

            if (allDuplicateLeads.length > 0) {
                setDuplicateLeads(allDuplicateLeads);
            }

            // Show results
            if (successCount > 0) {
                let message = `Successfully imported ${successCount} leads!`;
                if (totalDuplicateCount > 0) {
                    message += ` ${totalDuplicateCount} duplicate(s) skipped. Review them below.`;
                }
                toast.success(message);
                if (onSuccess) {
                    onSuccess();
                }
            } else if (totalDuplicateCount > 0) {
                toast.warning(`All ${totalDuplicateCount} leads were duplicates and skipped. Review them below.`);
            }

            if (errorCount > 0) {
                toast.warning(`${errorCount} leads failed to import`);
            }

            // Don't close modal if there are duplicates to review
            if (totalDuplicateCount > 0) {
                // Keep modal open so user can review duplicates
                return;
            }

            // Close modal after successful import (only if no duplicates)
            if (successCount > 0) {
                setTimeout(() => {
                    onClose();
                }, 1500);
            }
        } catch (error) {
            console.error("Import error:", error);
            toast.error("Failed to import leads");
        } finally {
            setImporting(false);
            setUploadProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleReset = () => {
        setFile(null);
        setCsvData([]);
        setValidationResults([]);
        setShowPreview(false);
        setIsDragging(false);
        setDuplicateLeads([]); // Clear duplicate leads on reset
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const downloadTemplate = () => {
        const csvContent = `First Name,Last Name,Email,Phone,Second Phone,Country,Status,Desk,Extra,Internal Notes
John,Doe,john@example.com,+1234567890,+1234567800,United States,New,Sales Desk A,Lead from Facebook,High priority client
Jane,Smith,jane@example.com,441234567890,441234567801,United Kingdom,Follow-Up,Sales Desk B,Follow up needed,Needs special attention
Olivia,Bennett,olivia.bennett@example.com,+34612345678,34612345679,Spain,Interested,Sales Desk A,Requested demo,
Mason,Carter,mason.carter@example.com,33123456789,+33123456780,France,Not Interested,Sales Desk B,Inbound query,
Ava,Reynolds,ava.reynolds@example.com,4912345678901,+4912345678902,Germany,Converted,Sales Desk A,Converted successfully,VIP contact`;

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "lead_import_template.csv";
        link.click();
        window.URL.revokeObjectURL(url);

        toast.success("Template downloaded successfully");
    };


    return (
        <>
            <Dialog open={true} onOpenChange={onClose}>
                <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-full overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center">
                            <Upload className="h-5 w-5 mr-2" />
                            Import Leads from CSV
                        </DialogTitle>
                        <DialogDescription>
                            Upload a CSV file to import multiple leads at once. Make sure your file follows the correct
                            format.
                        </DialogDescription>
                        {/* Template Download */}
                        <div className="pt-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <FileText className="h-5 w-5 text-blue-600 mr-2" />
                                        <div>
                                            <h4 className="font-medium text-blue-900">Download Template</h4>
                                            <p className="text-sm text-blue-700">
                                                Get the correct CSV format for importing leads
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        onClick={downloadTemplate}
                                        className="text-blue-600 border-blue-600"
                                    >
                                        <Download className="h-4 w-4 mr-2" />
                                        Download
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="space-y-6">
                        {/* File Upload */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                {file && (
                                    <Button variant="outline" size="sm" onClick={handleReset}>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Reset
                                    </Button>
                                )}
                            </div>
                            <div
                                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                                    isDragging
                                        ? "border-blue-500 bg-blue-50"
                                        : file
                                          ? "border-green-300 bg-green-50"
                                          : "border-gray-300 bg-white hover:border-gray-400"
                                }`}
                                onDragEnter={handleDragEnter}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    id="fileUpload"
                                />

                                {file ? (
                                    <div className="space-y-2">
                                        <FileText className="h-12 w-12 text-green-600 mx-auto" />
                                        <p className="font-medium">{file.name}</p>
                                        <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                                        {showPreview && (
                                            <div className="flex items-center justify-center space-x-4 mt-4">
                                                <div className="flex items-center space-x-2">
                                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                                    <span className="text-sm font-medium text-green-600">
                                                        {validationResults.length} Rows Ready
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        <Upload
                                            className={`h-12 w-12 mx-auto ${
                                                isDragging ? "text-blue-500" : "text-gray-400"
                                            }`}
                                        />
                                        <p
                                            className={
                                                isDragging ? "text-blue-600 font-medium" : "text-gray-600"
                                            }
                                        >
                                            {isDragging
                                                ? "Drop your CSV file here"
                                                : "Click to select a CSV file or drag and drop"}
                                        </p>
                                        <Button
                                            variant="outline"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={importing}
                                        >
                                            Select File
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Upload Progress Bar */}
                        {importing && uploadProgress.total > 0 && (
                            <div className="space-y-2 bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center space-x-2">
                                        <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
                                        <span className="text-sm font-medium text-blue-900">
                                            Uploading leads...
                                        </span>
                                    </div>
                                    <span className="text-sm font-medium text-blue-700">
                                        {uploadProgress.current} / {uploadProgress.total} ({uploadProgress.percentage}%)
                                    </span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-2.5">
                                    <div
                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                                        style={{ width: `${uploadProgress.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        )}

                        {/* Preview Table */}
                        {showPreview && validationResults.length > 0 && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-medium">Preview (First 10 rows)</h4>
                                    <div className="text-sm text-gray-600">
                                        Total: {validationResults.length} rows
                                    </div>
                                </div>

                                <div className="border rounded-lg max-h-96 overflow-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Row</TableHead>
                                                <TableHead>First Name</TableHead>
                                                <TableHead>Last Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Phone</TableHead>
                                                <TableHead>Second Phone</TableHead>
                                                <TableHead>Country</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Desk</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {validationResults.slice(0, 10).map((result, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{result.rowIndex}</TableCell>
                                                    <TableCell>{result.lead.firstName || "-"}</TableCell>
                                                    <TableCell>{result.lead.lastName || "-"}</TableCell>
                                                    <TableCell>{result.lead.email || "-"}</TableCell>
                                                    <TableCell>{result.lead.phone || "-"}</TableCell>
                                                    <TableCell>{result.lead.secondPhone || "-"}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center space-x-2">
                                                            <span>{result.lead.country || "-"}</span>
                                                            {result.lead.detectedFromPhone && (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="text-xs bg-green-100 text-green-800"
                                                                >
                                                                    Auto-detected
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {leadStatuses.find(
                                                            (option) => option.value === result.lead.status
                                                        )?.label || "-"}
                                                    </TableCell>
                                                    <TableCell>
                                                        {availableDesks.find((desk) => desk._id === result.lead.desk)
                                                            ?.name || "-"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Auto-Detection Summary */}
                        {showPreview && validationResults.length > 0 && (
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h4 className="font-medium text-green-900 mb-2">Auto-Detection Summary</h4>
                                <div className="grid grid-cols-2 md:grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-green-600">Total Rows:</span>
                                        <span className="ml-2 font-medium">{validationResults.length}</span>
                                    </div>
                                    <div>
                                        <span className="text-green-600">Phones Processed:</span>
                                        <span className="ml-2 font-medium">
                                            {validationResults.filter((r) => r.lead.phone).length}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Assignment Options (for Admin) */}
                        {showAssignmentOptions && isAdmin && (
                            <div className="space-y-4">
                                <Card>
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-base">Assignment Type</CardTitle>
                                        <CardDescription>Choose how to assign the imported leads</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <RadioGroup
                                            value={assignmentType}
                                            onValueChange={(value) => {
                                                setAssignmentType(value);
                                                setSelectedAgents([]);
                                                setSelectedDesk("");
                                                setSelectedFloorManager("");
                                                // Fetch floor managers if not already loaded when floorManager is selected
                                                if (value === "floorManager") {
                                                    if (availableFloorManagers.length === 0) {
                                                        fetchFloorManagers();
                                                    }
                                                }
                                            }}
                                            className="flex flex-col space-y-3"
                                        >
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="agent" id="import-agent" />
                                                <Label htmlFor="import-agent" className="text-sm font-normal">
                                                    <div className="flex items-center">
                                                        <Users className="h-4 w-4 mr-2" />
                                                        Assign to Agent(s)
                                                    </div>
                                                </Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="desk" id="import-desk" />
                                                <Label htmlFor="import-desk" className="text-sm font-normal">
                                                    <div className="flex items-center">
                                                        <Building2 className="h-4 w-4 mr-2" />
                                                        Assign to Desk
                                                    </div>
                                                </Label>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <RadioGroupItem value="floorManager" id="import-floorManager" />
                                                <Label htmlFor="import-floorManager" className="text-sm font-normal">
                                                    <div className="flex items-center">
                                                        <UserCog className="h-4 w-4 mr-2" />
                                                        Assign to Floor Manager
                                                    </div>
                                                </Label>
                                            </div>
                                        </RadioGroup>
                                    </CardContent>
                                </Card>

                                {/* Agent Selection */}
                                {assignmentType === "agent" && (
                                    <div className="space-y-3">
                                        <Label>Select Agents</Label>
                                        <MultiSelect
                                            options={availableAgents.map((agent) => ({
                                                label: getFullName(agent),
                                                value: agent._id
                                            }))}
                                            selected={selectedAgents}
                                            onChange={setSelectedAgents}
                                            placeholder="Select agents to assign leads"
                                            searchPlaceholder="Search agents..."
                                        />
                                        {selectedAgents.length > 0 && (
                                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                                <p className="text-sm text-green-800">
                                                    <strong>{validationResults.filter((r) => r.isValid).length} leads</strong>{" "}
                                                    will be distributed among <strong>{selectedAgents.length} agents</strong>.
                                                    Each agent will receive approximately{" "}
                                                    <strong>
                                                        {Math.ceil(
                                                            validationResults.filter((r) => r.isValid).length /
                                                                selectedAgents.length
                                                        )}{" "}
                                                        leads
                                                    </strong>
                                                    .
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Desk Selection */}
                                {assignmentType === "desk" && (
                                    <div className="space-y-3">
                                        <Label>Select Desk</Label>
                                        <Select
                                            value={selectedDesk}
                                            onChange={setSelectedDesk}
                                            options={availableDesks.map((desk) => ({
                                                value: desk._id,
                                                label: desk.name
                                            }))}
                                            placeholder="Choose a desk to assign leads to"
                                            searchable={true}
                                            searchPlaceholder="Search desks..."
                                        />
                                        {selectedDesk && (
                                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                                <p className="text-sm text-green-800">
                                                    <strong>{validationResults.filter((r) => r.isValid).length} leads</strong>{" "}
                                                    will be assigned to the selected desk.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Floor Manager Selection */}
                                {assignmentType === "floorManager" && (
                                    <div className="space-y-3">
                                        <Label>Select Floor Manager</Label>
                                        {availableFloorManagers.length > 0 ? (
                                            <>
                                                <Select
                                                    value={selectedFloorManager}
                                                    onChange={setSelectedFloorManager}
                                                    options={availableFloorManagers.map((floorManager) => ({
                                                        value: floorManager._id,
                                                        label: `${getFullName(floorManager)} - ${
                                                            floorManager.position?.name || floorManager.position?.role
                                                        }`
                                                    }))}
                                                    placeholder="Choose a floor manager to assign leads to"
                                                    searchable={true}
                                                    searchPlaceholder="Search floor managers by name..."
                                                />
                                                {selectedFloorManager && (
                                                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                                                        <p className="text-sm text-green-800">
                                                            <strong>{validationResults.filter((r) => r.isValid).length} leads</strong>{" "}
                                                            will be assigned to the selected floor manager.
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <p className="text-sm text-yellow-800">
                                                    No floor managers found. Please create a floor manager first.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="flex space-x-3">
                                    <Button
                                        onClick={() => handleAssignLeads(validationResults.filter((r) => r.isValid))}
                                        disabled={
                                            importing ||
                                            (assignmentType === "agent" && selectedAgents.length === 0) ||
                                            (assignmentType === "desk" && !selectedDesk) ||
                                            (assignmentType === "floorManager" && !selectedFloorManager)
                                        }
                                        className="flex-1 "
                                    >
                                        {importing ? (
                                            <>
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                Importing...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                Import and Assign
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setShowAssignmentOptions(false);
                                            processImport(validationResults.filter((r) => r.isValid));
                                        }}
                                        disabled={importing}
                                        className="flex-1"
                                    >
                                        Skip Assignment and Import
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Assignment Options (for Managers) */}
                        {showAssignmentOptions && user.position.role === "deskManager" && (
                            <div className="space-y-4">
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                    <div className="flex items-center mb-3">
                                        <Users className="h-5 w-5 text-blue-600 mr-2" />
                                        <h4 className="font-medium text-blue-900">Assign Leads to Agents</h4>
                                    </div>
                                    <p className="text-sm text-blue-800 mb-4">
                                        Select agents to distribute {validationResults.filter((r) => r.isValid).length}{" "}
                                        leads evenly among them.
                                    </p>

                                    <div>
                                        <Label>Select Agents</Label>
                                        <MultiSelect
                                            options={availableAgents.map((agent) => ({
                                                label: getFullName(agent),
                                                value: agent._id
                                            }))}
                                            selected={selectedAgents}
                                            onChange={setSelectedAgents}
                                            placeholder="Select agents to assign leads"
                                            searchPlaceholder="Search agents..."
                                        />

                                        {selectedAgents.length > 0 && (
                                            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                                                <p className="text-sm text-green-800">
                                                    <strong>
                                                        {validationResults.filter((r) => r.isValid).length} leads
                                                    </strong>{" "}
                                                    will be distributed among{" "}
                                                    <strong>{selectedAgents.length} agents</strong>.
                                                    {selectedAgents.length > 0 && (
                                                        <span>
                                                            {" "}
                                                            Each agent will receive approximately{" "}
                                                            <strong>
                                                                {Math.ceil(
                                                                    validationResults.filter((r) => r.isValid).length /
                                                                        selectedAgents.length
                                                                )}{" "}
                                                                leads
                                                            </strong>
                                                            .
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex space-x-3">
                                    <Button
                                        onClick={() => handleAssignLeads(validationResults.filter((r) => r.isValid))}
                                        disabled={selectedAgents.length === 0 || importing}
                                        className="flex-1 "
                                    >
                                        {importing ? (
                                            <>
                                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                                Assigning...
                                            </>
                                        ) : (
                                            <>
                                                <Users className="h-4 w-4 mr-2" />
                                                Assign and Import
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setShowAssignmentOptions(false);
                                            processImport(validationResults.filter((r) => r.isValid));
                                        }}
                                        disabled={importing}
                                        className="flex-1"
                                    >
                                        Skip Assignment and Import
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Import Loading */}
                        {importing && (
                            <div className="flex items-center justify-center space-x-3 p-6 bg-blue-50 rounded-lg border border-blue-200">
                                <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                                <span className="text-sm font-medium text-blue-800">Importing leads...</span>
                            </div>
                        )}
                    </div>

                    {/* Important Notes - Always at the bottom */}
                    <div className="pt-6 flex flex-col justify-end">
                        <div className="bg-yellow-50 rounded-lg mb-6 border border-yellow-200">
                            <button
                                type="button"
                                onClick={() => setNotesExpanded(!notesExpanded)}
                                className="flex items-center justify-between w-full p-4 text-left"
                            >
                                <div className="flex items-center">
                                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                                    <span className="text-sm font-medium text-yellow-800">Important Notes</span>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-yellow-600 transition-transform duration-200 ${notesExpanded ? "rotate-180" : ""}`} />
                            </button>
                            {notesExpanded && (
                                <div className="px-4 pb-4 pt-0">
                                    <ul className="list-disc list-inside text-sm text-yellow-700 space-y-1 ml-7">
                                        <li>CSV file should have headers in the first row</li>
                                        <li>
                                            <strong>First Name or Last Name</strong> and <strong>Phone</strong> are required fields
                                        </li>
                                        <li>
                                            <strong>Phone numbers</strong> can include country code with or without +
                                            sign (e.g., +1, +44, +49 or 1, 44, 49). Numbers without + will be
                                            automatically normalized.
                                        </li>
                                        <li>
                                            <strong>Excel Formatting Warning:</strong> If your phone numbers appear as scientific notation (e.g., 4.47E11) in Excel, format the Phone column as <strong>Text</strong> before saving as CSV.
                                        </li>
                                        <li>
                                            <strong>Country auto-detection:</strong> Countries will be automatically
                                            detected from phone numbers
                                        </li>
                                        {(user.position.role === "deskManager" || isAdmin) && (
                                            <li>
                                                <strong>After import:</strong> You&apos;ll have the option to assign
                                                leads to {isAdmin ? "agents, desks, or floor managers" : "agents"}
                                            </li>
                                        )}
                                        <li>Email addresses must be valid format (if provided)</li>
                                        <li>Invalid rows will be skipped with confirmation</li>
                                        <li>Maximum file size: 20MB</li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Duplicate Leads Review Section */}
                        <DuplicateReviewPanel
                            duplicateLeads={duplicateLeads}
                            leadStatuses={leadStatuses}
                            availableAgents={availableAgents}
                            availableFloorManagers={availableFloorManagers}
                            availableGeneralManagers={availableGeneralManagers}
                            availableDesks={availableDesks}
                            importing={importing}
                            setImporting={setImporting}
                            onSuccess={onSuccess}
                        />

                        {/* Action Buttons - Always at the bottom */}
                        <div className="flex space-x-4">
                            {duplicateLeads.length > 0 ? (
                                <>
                                    <Button onClick={() => { onSuccess?.(); onClose(); }}>
                                        Finish
                                    </Button>
                                    {createdLeadIds.length > 0 && (
                                        <Button
                                            variant="destructive"
                                            onClick={async () => {
                                                setDeleting(true);
                                                try {
                                                    const res = await bulkDeleteLeads(createdLeadIds);
                                                    if (res?.status === 200) {
                                                        toast.success(`Deleted ${createdLeadIds.length} uploaded lead(s)`);
                                                        setCreatedLeadIds([]);
                                                        onSuccess?.();
                                                    } else {
                                                        toast.error("Failed to delete uploaded leads");
                                                    }
                                                } catch (err) {
                                                    console.error("Delete error:", err);
                                                    toast.error("Failed to delete uploaded leads");
                                                } finally {
                                                    setDeleting(false);
                                                }
                                            }}
                                            disabled={deleting}
                                        >
                                            {deleting ? "Deleting..." : `Delete ${createdLeadIds.length} Uploaded Lead(s)`}
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    {!(showAssignmentOptions && (user.position.role === "deskManager" || isAdmin)) && (
                                        <Button
                                            onClick={handleImport}
                                            disabled={!showPreview || validationResults.length === 0 || importing}
                                        >
                                            {importing ? "Importing..." : (
                                                <>
                                                    Next
                                                    <ArrowRight className="h-4 w-4 ml-2" />
                                                </>
                                            )}
                                        </Button>
                                    )}
                                    <Button variant="outline" onClick={onClose} disabled={importing}>
                                        Cancel
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
